import type { components } from '@connected/api-client/api.generated';

import { useAuthentication } from '@connected/api-client/hooks/use-authentication';
import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { ScrollArea } from '@connected/ui/components/scroll-area';
import { Separator } from '@connected/ui/components/separator';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { icon } from 'leaflet';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { z } from 'zod';

import mapPin from '@/assets/map-pin.svg';
import { CustomerLocationsHeader } from '@/components/customer-locations-header';
import { ObjectCard } from '@/components/object-card';
import { ResizableContainer } from '@/components/resizable-container';
import { cn } from '@/lib/utils';

const customIcon = icon({
    iconUrl: mapPin,
    iconSize: [30, 30],
    iconAnchor: [14, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});

function MarkerWithHover({ position, children }: { position: [number, number]; children: React.ReactNode }) {
    const markerRef = useRef<any>(null);

    return (
        <Marker
            ref={markerRef}
            position={position}
            icon={customIcon as any}
            eventHandlers={{
                mouseover: () => {
                    markerRef.current?.openPopup();
                },
            }}
        >
            <Popup closeButton={true} autoClose={true}>
                {children}
            </Popup>
        </Marker>
    );
}

export const Route = createFileRoute('/_auth/customer/locations/')({
    component: RouteComponent,
    validateSearch: z.object({
        search: z.string().optional(),
        organizationId: z.string().optional(),
    }),
});

export type ObjectKind = components['schemas']['ListObjectPortalQueryResultModule'];

function RouteComponent() {
    const { t } = useTranslation('customer-locations');
    const search = Route.useSearch();

    const resizableContainerTarget = useRef<HTMLDivElement>(null);
    const [mapKey, setMapKey] = useState(Date.now());

    const { currentUser } = useAuthentication();

    const organizationId = search.organizationId ?? currentUser?.Organization?.OrganizationId ?? null;

    const objectsQuery = useQuery(portaalQueryOptions.portal.objects.list({
        OrganizationId: organizationId,
    }));

    const filteredData = useMemo(() => {
        if (search.search) {
            return objectsQuery.data?.Objects.filter(
                x =>
                    ((x.Locality ?? '') + (x.PostalCode ?? ''))
                        .toLowerCase()
                        .includes(search.search?.toLowerCase() || '')
                        || (x.Street ?? '').toLowerCase().includes(search.search?.toLowerCase() || ''),
            );
        }
        return objectsQuery.data?.Objects;
    }, [objectsQuery.data?.Objects, search.search]);

    return (
        <div className="max-w-screen-2xl flex flex-col flex-1 px-4 pt-4 sm:px-8 sm:pt-8">

            <CustomerLocationsHeader route={Route} />
            <Separator />
            <div className="py-4 w-full h-full">
                <ResizableContainer
                    targetElement={resizableContainerTarget}
                    resizeFunction={() => setMapKey(Date.now())}
                >
                    <div
                        className="flex flex-col lg:flex-1 lg:flex-row-reverse gap-2 xl:gap-4"
                        ref={resizableContainerTarget}
                    >
                        <div className="w-full lg:w-1/2 max-sm:h-[100vh] max-lg:h-[150vh] z-10 hidden lg:block relative">
                            <MapContainer
                                key={mapKey}
                                center={[52.1326, 5.2913]}
                                zoom={7}
                                className="h-full rounded-xl"
                            >
                                <TileLayer
                                    attribution='&copy; <a href="https://carto.com/attributions">CARTO</a>'
                                    url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png"
                                />
                                <MarkerClusterGroup
                                    showCoverageOnHover
                                    disableClusteringAtZoom={18}
                                    spiderfyOnMaxZoom={false}
                                    removeOutsideVisibleBounds
                                    maxClusterRadius={80}
                                >
                                    {filteredData?.map(object => (
                                        object.Coordinate?.GpsLatitude && object.Coordinate?.GpsLongitude && (
                                            <MarkerWithHover
                                                key={object.ObjectId}
                                                position={[object.Coordinate.GpsLatitude, object.Coordinate.GpsLongitude]}
                                            >
                                                <div className="text-sm">
                                                    <strong>
                                                        {object.Locality}
                                                        {' '}
                                                        {object.PostalCode}
                                                    </strong>
                                                    <br />
                                                    {object.Street}
                                                    {' '}
                                                    {object.StreetNumber}
                                                </div>
                                            </MarkerWithHover>
                                        )
                                    ))}
                                </MarkerClusterGroup>
                            </MapContainer>
                        </div>
                        <ScrollArea className="w-full lg:w-1/2">
                            <div className="grid md:grid-cols-2 grid-cols-1 gap-4 lg:flex lg:flex-col lg:snap-y lg:snap-mandatory overflow-y-auto">
                                {filteredData?.map(object => (
                                    <ObjectCard
                                        key={object.ObjectId}
                                        className="snap-center"
                                        objectId={object.ObjectId ?? ''}
                                    >
                                        <ObjectCard.Header className="mb-3" objectId={object.ObjectId ?? ''}>
                                            <ObjectCard.Title>
                                                {object.Locality}
                                                {' '}
                                                {object.PostalCode}
                                            </ObjectCard.Title>
                                            <ObjectCard.SubTitle>
                                                {object.Street}
                                                {' '}
                                                {object.StreetNumber}
                                            </ObjectCard.SubTitle>
                                        </ObjectCard.Header>
                                        <ObjectCard.Content>
                                            <ObjectCard.Image
                                                className={cn('rounded-md w-full lg:w-1/2 object-cover overflow-clip aspect-[4/3]', {
                                                    'bg-muted text-text-primary/50 border-2 border-dashed': !object.HasImage,
                                                })}
                                                objectId={object.ObjectId ?? ''}
                                                queryOptions={portaalQueryOptions.objects.download({
                                                    ObjectId: object.ObjectId ?? '',
                                                })}
                                                enabled={!!object.HasImage}
                                            />
                                            <ObjectCard.Modules>
                                                {transformModulesWithPreviews(object.Modules)?.map(
                                                    (index, moduleIndex) => (
                                                        <div
                                                            key={`${index}`}
                                                            className={moduleIndex !== (transformModulesWithPreviews(object.Modules)?.length ?? 0) - 1 ? 'mb-3' : ''}
                                                        >
                                                            <ObjectCard.Module
                                                                className="p-5"
                                                                moduleType={index.Kind?.toLowerCase() ?? ''}
                                                                objectId={object.ObjectId ?? ''}
                                                                isPreview={index.isPreview}
                                                                observations={(index.Issues ?? [])
                                                                    .filter(issue => issue.Count !== -1)
                                                                    .map(issue => ({
                                                                        label: issue.Kind ?? '',
                                                                        color: issue.HexColorCode ?? '',
                                                                        children: issue.Count ?? 0,
                                                                    }))}
                                                            >
                                                                <ObjectCard.Module.Title className="pl-1 pb-1 text-left text-md">
                                                                    {index.Kind ? t(`locations.modules.${index.Kind?.toLowerCase()}.title`) : null}
                                                                </ObjectCard.Module.Title>
                                                                <ObjectCard.Module.Observations
                                                                    className="justify-left"
                                                                    observations={
                                                                        index.Issues?.length
                                                                            ? index.Issues
                                                                                    .filter(issue => issue.Count !== -1)
                                                                                    .map(issue => ({
                                                                                        label: index.Kind
                                                                                            ? t(`locations.modules.${index.Kind.toLowerCase()}.observations.${issue.Kind ?? ''}`)
                                                                                            : '',
                                                                                        color: issue.HexColorCode ?? '',
                                                                                        children: issue.Count ?? 0,
                                                                                    }))
                                                                            : (
                                                                                    index.isPreview
                                                                                        ? [{ label: '', color: '', isPreview: true, children: 'Preview' }]
                                                                                        : []
                                                                                )

                                                                    }
                                                                />
                                                            </ObjectCard.Module>
                                                        </div>
                                                    ),
                                                )}
                                            </ObjectCard.Modules>
                                        </ObjectCard.Content>
                                    </ObjectCard>
                                ))}
                                {!filteredData?.length && (
                                    <p className="text-center py-6">
                                        {t('locations.objects.no-results')}
                                    </p>
                                )}
                            </div>
                        </ScrollArea>
                    </div>
                </ResizableContainer>
            </div>
        </div>
    );
}

type ModuleType = {
    Kind?: 'Electrical' | null | undefined;
    Issues?: {
        Kind?: 'ElectricalProjectObservationSevere' | 'ElectricalProjectObservationSerious' | 'ElectricalProjectObservationMinor' | 'ElectricalProjectObservationRemark' | 'ElectricalTicket' | 'ElectricalRequest' | null | undefined;
        Count?: number | null | undefined;
        HexColorCode?: string | null | undefined;
    }[] | null | undefined;
}[] | null | undefined;

function transformModulesWithPreviews(modules: ModuleType): (ObjectKind & { isPreview: boolean })[] {
    const allModules = [
        'Electrical',
        'Atex',
        // 'Solar',
        'Air',
        'Water',
        'Legionella',
        // 'Cooling',
    ] as const;

    return allModules.map((moduleKind) => {
        const foundModule = modules?.find(m => m.Kind === moduleKind);
        if (foundModule) {
            return {
                ...foundModule,
                isPreview: false,
            } as ObjectKind & { isPreview: boolean };
        }
        return {
            Kind: moduleKind as any,
            Issues: [],
            isPreview: true,
        } as ObjectKind & { isPreview: boolean };
    });
}
